import { trigger, state, style, transition, animate } from '@angular/animations';
import { formatNumber } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, HostListener, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { DialogConfirmComponent } from 'src/app/dialog/dialog-confirm/dialog-confirm.component';
import { ComponentCanDeactivate } from 'src/app/_guard/pending-changes.guard';
import { AppService, GRSSResponse } from 'src/app/_services/app.service';

@Component({
    selector: 'app-assess',
    templateUrl: './assess.component.html',
    styleUrls: ['./assess.component.css'],
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})
export class AssessComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  marketId: string;

  assessMarket: AssessMarket;
  assessMarketTemp: AssessMarket;

  contributionsLength: number = 0;
  instrumentLength: number = 0;
  timeToOpenOrClose: string;
  displayColumnsAll: string[] = ['name', 'comments', 'assessedRate', 'assessedVolume', 'currentRate', 'currentVolume', 'notes', 'actions'];
  displayColumns: string[] = ['name', 'comments'];
  dataSourceNew;
  currentTimeColumn: string = '';
  intervals: any[] = [];

  discardChanges: boolean = false;
  canEnableSubmit: boolean = false;

  allowSubmissions: boolean = false;
  isLoading: boolean = false;

  // tslint:disable-next-line: max-line-length
  constructor(private translate: TranslateService, @Inject(LOCALE_ID) public localeID: string, private router: Router, private dialog: MatDialog, private route: ActivatedRoute, public service: AppService, private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
    this.service.SetTranslation('my-general-markets/assess');
  }

  ngOnInit() {
    this.InitializeMarket();
    this.route.queryParams.subscribe(params => {
      this.assessMarket.marketId = params['ID'];
      this.CheckMarketType(this.assessMarket.marketId).then(canProceed => {
        if (canProceed === true) {
          this.GetContributions();
          this.CheckOverrideWindow();
          const interval = setInterval(() => {
            this.service.GetRemainingTime(this.assessMarket.marketId, 'Contribution StartOrFinish').then(res => {
              this.timeToOpenOrClose = res.time;
              if (this.currentTimeColumn === '') {
                this.currentTimeColumn = res.timeColumn;
              } else if ((this.currentTimeColumn !== res.timeColumn) || res.time.split('0').join('').replace(/\:/gi, '').trim() === '') {
                if (this.currentTimeColumn === 'Contribution Start' && res.timeColumn === 'Contribution Finish') {
                  this.GetContributions();
                } else if ((this.currentTimeColumn === 'Contribution Finish' && res.timeColumn === 'Contribution Start') || (res.timeColumn === 'Contribution Finish' && this.allowSubmissions === false)) {
                  this.assessMarket.isMarketOpen = false;
                  this.assessMarketTemp.isMarketOpen = false;
                }
                this.currentTimeColumn = res.timeColumn;
              }
            });
          }, 1000);
          this.intervals.push(interval);
        } else {
          this.discardChanges = true;
          this.router.navigate(['contribute/assessTransact'], { queryParams: {ID: this.assessMarket.marketId} });
        }
      });
    });
  }

  ngOnDestroy() {
    if (this.intervals.length > 0) {
      this.intervals.forEach(interval => {
        clearInterval(interval);
      });
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    let hasChanges: boolean = false;

    if (JSON.stringify(this.assessMarket) !== JSON.stringify(this.assessMarketTemp) && this.discardChanges === false) {
      hasChanges = true;
    }

    return !hasChanges;
  }

  /**
   *  Checks the market type
   * @param marketId Selected market ig
   */
  CheckMarketType(marketId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.service.currentUser.userAssessMarkets.find(e => e.marketId === marketId).isTransactional === false) {
        resolve(true);
      } else {
        resolve(false);
      }
    });
  }

  
  /**
   * Check if the window override enabled to allow bypass contribution window times and submit contributions
   */
  CheckOverrideWindow() {
    this.http.get<boolean>(this.baseUrl + 'api/contribute/checkWindowOpen/submission/' + this.assessMarket.marketId).subscribe(result => {
      this.allowSubmissions = result;
    }, error => {
      console.error(error);
    });
  }

  /** Initializes the market details */
  InitializeMarket() {
    this.assessMarket = {
      marketId: '',
      marketCode: '',
      isMarketOpen: false,
      isAllowedUser: false,
      instruments: null,
      conflicted: false,
      canAssess: false
    };
  }

  /** Checks the form inputs */
  CheckInputs(): boolean {
    let hasNotes: boolean = false;
    this.assessMarket.instruments.forEach(o => {
      if (o.newNotes.trim() !== '') {
        hasNotes = true;
      }
    });
    return hasNotes;
  }

  /** Gets the contributions for the market */
  GetContributions() {
    this.isLoading = true;
    this.service.showSpinner();
    this.http.get<AssessMarket>(this.baseUrl + 'api/assess/getContributions/' + this.assessMarket.marketId).subscribe(result => {

      this.assessMarket = result;
      this.displayColumns = this.displayColumnsAll;

      let hasVolume: boolean = false;
      let hasAssessed: boolean = false;
      let hasCurrentRate: boolean = false;

      // Generating dynamic columns
      if (this.assessMarket.instruments.length > 0) {
        this.assessMarket.instruments.forEach(i => {
          let temp = i.rate.replace('-', '');
          let beforeDec = temp.split('.')[0].length.toString();
          i.rate = formatNumber(Number(i.rate), this.localeID, beforeDec + '.' + i.decimalPlaces + '-' + i.decimalPlaces);
          if (i.level === 0 && i.assessedRate) {
            i.assessedRate = formatNumber(Number(i.assessedRate), this.localeID, beforeDec + '.' + i.decimalPlaces + '-' + i.decimalPlaces);
          }
          
          if (i.previousRate) {
            const temp = i.previousRate.replace('-', '');
            const parts = temp.split('.');
            const beforeDec = parts[0].length.toString();
            const afterDec = parts[1] ? parts[1].length.toString() : '0';
            i.previousRate = formatNumber(Number(i.previousRate), this.localeID, beforeDec + '.' + afterDec + '-' + afterDec);
          }

          if (i.lastAccepted === true || i.instrumentAccepted === true) {
            i.accepted = 'true';
          } else {
            i.accepted = 'null';
          }

          i.newNotes = '';
          if (i.calculationType === 'wav') {
            hasVolume = true;
          }

          if (i.assessedRate) {
            hasAssessed = true;
          }

          if (i.rate) {
            hasCurrentRate = true;
          }
        });
      }

      if (hasVolume === false) {
        const volumeItems: string[] = ['assessedVolume', 'currentVolume', 'newVolume'];
        this.displayColumns = this.displayColumns.filter(f => !volumeItems.includes(f));
      }

      if (hasAssessed === false) {
        const assessedItems: string[] = ['assessedRate', 'assessedVolume'];
        this.displayColumns = this.displayColumns.filter(f => !assessedItems.includes(f));
      }

      if (hasCurrentRate === false) {
        const currentItems: string[] = ['currentRate', 'currentVolume'];
        this.displayColumns = this.displayColumns.filter(f => !currentItems.includes(f));
      }


      this.assessMarketTemp = JSON.parse(JSON.stringify(this.assessMarket));

      this.instrumentLength = this.assessMarket.instruments.length;
      this.dataSourceNew = new MatTableDataSource(this.assessMarket.instruments);

      this.OnClickAction();
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
      this.isLoading = false;
    });
  }

  /** Click event for the reject button */
  OnRejectClick() {
    const hasNotes = this.CheckInputs();
    if (hasNotes === false) {
      // this.SaveStatus(false, 'Please provide some notes for the rates being rejected.');
    } else {
      this.dialog.open(DialogConfirmComponent, {
        disableClose: true,
        data: {
          title: 'Confirm Rejection',
          message: 'Are you sure you want to reject these rates?'
        },
        position: { top: '5%' }
      }).afterClosed().subscribe(res => {
        // Evaluate the users response.
        if (res === true) {
          this.assessMarket.instruments.forEach(i => {
            i.notes = i.newNotes;
          });
          this.http.post<GRSSResponse>(this.baseUrl + 'api/assess/reject', this.assessMarket).subscribe(result => {
            if (result.statusCode === 1) {
              // this.SaveStatus(false, result.statusMessage);
              this.GetContributions();
            } else if (result.statusCode === 4) {
              // this.SaveStatus(true, result.statusMessage);
            } else {
              // this.SaveStatus(false, result.statusMessage);
            }
            this.assessMarket.instruments.forEach(i => {
              i.newNotes = i.notes;
            });
            this.assessMarketTemp.instruments.forEach(i => {
              i.newNotes = i.notes;
            });
          }, error => {
            console.error(error);
          });
        }
      });
    }
  }

  /** Click event for the accept event */
  OnAcceptClick() {
    this.http.post<GRSSResponse>(this.baseUrl + 'api/contribute/verifyRates', this.assessMarket).subscribe(re => {
      if (re.statusCode === 1) {
        this.dialog.open(DialogConfirmComponent, {
          disableClose: true,
          data: {
            title: 'Confirm Acceptance',
            message: '<label class="text-danger">' + re.statusMessage + '</label><br>Are you sure you want to accept these rates?',
            innerHTML: true
          },
          position: { top: '5%' }
        }).afterClosed().subscribe(res => {
          // Evaluate the users response.
          if (res === true) {
            this.assessMarket.instruments.forEach(i => {
              i.notes = i.newNotes;
            });
            this.http.post<GRSSResponse>(this.baseUrl + 'api/assess/accept', this.assessMarket).subscribe(result => {
              if (result.statusCode === 1) {
                this.GetContributions();
                this.service.SaveStatus(false, 'Contribute Rates', result.statusMessage);
              } else if (result.statusCode === 4) {
                this.service.SaveStatus(true, 'Contribute Rates', result.statusMessage);
              } else {
                this.service.SaveStatus(false, 'Contribute Rates', result.statusMessage);
              }
              this.assessMarket.instruments.forEach(i => {
                i.newNotes = i.notes;
              });
              this.assessMarketTemp.instruments.forEach(i => {
                i.newNotes = i.notes;
              });
            }, error => {
              console.error(error);
            });
          }
        });
      } else if (re.statusCode === 4) {
        this.service.SaveStatus(true, 'Contribute Rates', re.statusMessage);
      } else {
        this.service.SaveStatus(false, 'Contribute Rates', re.statusMessage);
      }
    }, error => {
      console.error(error);
    });
  }

  /** Submit button click event */
  OnSubmitClick() {
    if (this.canEnableSubmit === true && this.assessMarket.canAssess == true) {
      this.http.post<VerifyResponse>(this.baseUrl + 'api/contribute/verifyRates', this.assessMarket).subscribe(re => {
        if (re.response.statusCode === 1) {
          this.assessMarket.conflicted = re.conflicted;
          this.dialog.open(DialogConfirmComponent, {
            disableClose: true,
            data: {
              title: this.translate.instant('Assess.Messages.msgHeaderCnfrmAssess'),
              message: re.response.statusMessage + this.translate.instant('Assess.Messages.msgParaCnfrmAssess'),
              innerHTML: true
            },
            position: { top: '5%' }
          }).afterClosed().subscribe(res => {
            // Evaluate the users response.
            if (res === true) {
              this.assessMarket.instruments.forEach(i => {
                i.notes = i.newNotes;
              });
              this.service.showSpinner();
              this.http.post<GRSSResponse>(this.baseUrl + 'api/assess/submit', this.assessMarket).subscribe(result => {
                if (result.statusCode === 1) {
                  this.GetContributions();
                  this.canEnableSubmit = false;
                  this.service.SaveStatus(false, 'Assess.Messages.msgHeaderAssess', result.statusMessage);
                } else if (result.statusCode === 4) {
                  this.service.SaveStatus(true, 'Assess.Messages.msgHeaderAssess', result.statusMessage);
                } else {
                  this.service.SaveStatus(false, 'Assess.Messages.msgHeaderAssess', result.statusMessage);
                }
                this.assessMarket.instruments.forEach(i => {
                  i.newNotes = i.notes;
                });
                this.assessMarketTemp.instruments.forEach(i => {
                  i.newNotes = i.notes;
                });
              }, error => {
                console.error(error);
              }).add(() => {
                this.service.hideSpinner();
              });
            }
          });
        } else if (re.response.statusCode === 4) {
          this.service.SaveStatus(true, 'Assess.Messages.msgHeaderAssess', re.response.statusMessage);
        } else {
          this.service.SaveStatus(false, this.translate.instant('Assess.Messages.msgHeaderAssess'), re.response.statusMessage, true);
        }
      }, error => {
        console.error(error);
      });
    }
  }

  /** Click event for the assessing action toggler */
  OnClickAction() {
    let hasInvRejects = false;
    let hasRejects = false;
    let hasNulls = false;
    let allNulls = true;
    this.assessMarket.instruments.forEach(i => {
      if (i.accepted === 'false') {
        hasRejects = true;
        if (!i.newNotes) {
          hasInvRejects = true;
        }
      }

      if (i.accepted !== 'null') {
        allNulls = false;
      } else {
        hasNulls = true;
      }
    });

    if (hasInvRejects !== false || allNulls === true) {
      this.canEnableSubmit = false;
    } else if ((hasNulls === false && hasRejects === false) || (hasInvRejects === false && hasRejects !== false)) {
      this.canEnableSubmit = true;
    } else {
      this.canEnableSubmit = false;
    }
  }


}

/** An interface to access market details */
interface AssessMarket {
  marketId: string;
  marketCode: string;
  isMarketOpen: boolean;
  isAllowedUser: boolean;
  conflicted: boolean;
  canAssess: boolean;
  instruments: AssessMarketRecord[];
}

/** An interface to access a record of a market instriment */
interface AssessMarketRecord {
  submissionInstrumentId: string;
  instrumentId: string;
  instrumentDescription: string;
  level: number;
  rate?: string;
  volume?: number;
  comments: string;
  assessedRate?: string;
  assessedVolume?: number;
  previousRate?: string;
  previousVolume?: number;
  notes: string;
  status: string;
  calculationType: string;
  newNotes: string;
  accepted: string;
  decimalPlaces: number;
  instrumentAccepted: boolean;
  lastAccepted: boolean;
}

/** Interface to access the response from the verify rates */
interface VerifyResponse {
  conflicted: boolean;
  response: GRSSResponse;
}
